import * as Sentry from '@sentry/react';
import { useCallback, useEffect } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';

import { useToast } from '@/hooks/use-toast';

import type { SourceControlProvider } from './SourceControlIntegrationCard';

export type IntegrationSyncResult =
  | { enabled: false }
  | {
      enabled?: true;
      success: boolean;
      usersAdded: number;
      usersRemoved: number;
      errorMessage?: string;
      alreadyRunning?: boolean;
    };

function capitalize(key: string): string {
  return key.length === 0 ? key : key[0].toUpperCase() + key.slice(1);
}

interface UseIntegrationSyncHandlerArgs<TResult extends IntegrationSyncResult> {
  provider: SourceControlProvider;
  organizationId: string;
  syncMutation: UseMutationResult<TResult, unknown, void, unknown>;
}

export function useIntegrationSyncHandler<
  TResult extends IntegrationSyncResult,
>({
  provider,
  organizationId,
  syncMutation,
}: UseIntegrationSyncHandlerArgs<TResult>): () => Promise<void> {
  const { toast } = useToast();
  const { id: integrationKey, displayName } = provider;
  const errorTag = `${integrationKey}_sync`;

  return useCallback(async (): Promise<void> => {
    try {
      const result = await syncMutation.mutateAsync();
      if ('enabled' in result && result.enabled === false) {
        toast({
          title: 'Feature not available',
          description: `${displayName} integration is not currently enabled.`,
          variant: 'destructive',
        });
        return;
      }
      if (result.alreadyRunning) {
        toast({
          title: 'Sync already in progress',
          description:
            'A sync is already running. Please wait for it to complete.',
        });
        return;
      }
      if (result.success) {
        toast({
          title: 'Sync completed',
          description: `Added ${result.usersAdded} user${result.usersAdded !== 1 ? 's' : ''}, removed ${result.usersRemoved} user${result.usersRemoved !== 1 ? 's' : ''}.`,
        });
      } else {
        toast({
          title: 'Sync failed',
          description: result.errorMessage ?? 'An error occurred during sync.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { error_type: errorTag },
        extra: { organizationId },
      });
      toast({
        title: 'Sync failed',
        description: `Failed to sync ${displayName} users. Please try again.`,
        variant: 'destructive',
      });
    }
  }, [syncMutation, toast, displayName, errorTag, organizationId]);
}

interface UseSettledEmptySentryEffectArgs {
  isSettledEmpty: boolean;
  provider: SourceControlProvider;
  organizationId: string;
}

export function useSettledEmptySentryEffect({
  isSettledEmpty,
  provider,
  organizationId,
}: UseSettledEmptySentryEffectArgs): void {
  const integrationKey = provider.id;
  useEffect(() => {
    if (!isSettledEmpty) return;
    Sentry.captureMessage(
      `${capitalize(integrationKey)}Integration: settled-empty state (card hidden)`,
      {
        level: 'warning',
        tags: { integration: integrationKey },
        extra: { organizationId },
      }
    );
  }, [isSettledEmpty, integrationKey, organizationId]);
}
